import React, { useState, useEffect } from 'react';
import adminApi from '../../adminApi';
import styles from './SMSAdminPanel.module.css';

const SMSAdminPanel = () => {
  const [smsStats, setSmsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadSMSStats();
  }, []);

  const loadSMSStats = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getSMSStatistics();
      setSmsStats(response.data);
      setError('');
    } catch (err) {
      console.error('Error loading SMS stats:', err);
      setError('Failed to load SMS statistics');
    } finally {
      setLoading(false);
    }
  };

  const sendTestSMS = async () => {
    if (!testPhone.trim() || !testMessage.trim()) {
      setError('Please enter both phone number and message');
      return;
    }

    setSendingTest(true);
    try {
      await adminApi.sendTestSMS(testPhone, testMessage);
      setError('');
      setTestMessage('');
      setTestPhone('');
      alert('Test SMS sent successfully!');
    } catch (err) {
      console.error('Error sending test SMS:', err);
      setError(err.response?.data?.error || 'Failed to send test SMS');
    } finally {
      setSendingTest(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };


  if (loading) {
    return (
      <div className={styles.adminPanel}>
        <div className={styles.loading}>Loading SMS statistics...</div>
      </div>
    );
  }

  return (
    <div className={styles.adminPanel}>
      <h2>SMS Integration Status</h2>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* SMS Statistics */}
      <div className={styles.section}>
        <h3>SMS Usage Statistics</h3>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{smsStats?.totalSMS || 0}</div>
            <div className={styles.statLabel}>Total SMS Sent</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{formatCurrency(smsStats?.totalCostCents || 0)}</div>
            <div className={styles.statLabel}>Total Cost</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{smsStats?.verifiedUsers || 0}</div>
            <div className={styles.statLabel}>Verified Users</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{smsStats?.smsRemindersEnabled || 0}</div>
            <div className={styles.statLabel}>SMS Reminders Enabled</div>
          </div>
        </div>
      </div>

      {/* SMS Status Breakdown */}
      <div className={styles.section}>
        <h3>SMS Status Breakdown</h3>
        <div className={styles.statusBreakdown}>
          {smsStats?.statusStats?.map((status) => (
            <div key={status.status} className={styles.statusItem}>
              <span className={styles.statusName}>{status.status}</span>
              <span className={styles.statusCount}>{status.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className={styles.section}>
        <h3>Recent SMS Activity (Last 30 Days)</h3>
        <div className={styles.activityTable}>
          <div className={styles.tableHeader}>
            <div className={styles.tableCell}>Date</div>
            <div className={styles.tableCell}>SMS Count</div>
            <div className={styles.tableCell}>Cost</div>
          </div>
          {smsStats?.recentActivity?.length > 0 ? (
            smsStats.recentActivity.map((activity) => (
              <div key={activity.date} className={styles.tableRow}>
                <div className={styles.tableCell}>{new Date(activity.date).toLocaleDateString()}</div>
                <div className={styles.tableCell}>{activity.count}</div>
                <div className={styles.tableCell}>{formatCurrency(activity.cost_cents || 0)}</div>
              </div>
            ))
          ) : (
            <div className={styles.tableRow}>
              <div className={styles.tableCell} colSpan="3">No recent activity</div>
            </div>
          )}
        </div>
      </div>

      {/* Test SMS */}
      <div className={styles.section}>
        <h3>Send Test SMS</h3>
        <div className={styles.testForm}>
          <div className={styles.formGroup}>
            <label htmlFor="test-phone">Phone Number:</label>
            <input
              id="test-phone"
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+1234567890"
              className={styles.formInput}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="test-message">Message:</label>
            <textarea
              id="test-message"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Enter test message..."
              rows="3"
              className={styles.formTextarea}
            />
          </div>
          <button
            onClick={sendTestSMS}
            disabled={sendingTest || !testPhone.trim() || !testMessage.trim()}
            className={styles.primaryButton}
          >
            {sendingTest ? 'Sending...' : 'Send Test SMS'}
          </button>
        </div>
      </div>

      {/* Configuration Info */}
      <div className={styles.section}>
        <h3>Configuration</h3>
        <div className={styles.configInfo}>
          <div className={styles.configItem}>
            <strong>SMS Provider:</strong> AWS SNS
          </div>
          <div className={styles.configItem}>
            <strong>Cost per SMS:</strong> $0.0075
          </div>
          <div className={styles.configItem}>
            <strong>Region:</strong> {process.env.REACT_APP_AWS_REGION || 'us-east-1'}
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className={styles.section}>
        <button onClick={loadSMSStats} className={styles.secondaryButton}>
          Refresh Statistics
        </button>
      </div>
    </div>
  );
};

export default SMSAdminPanel;
